import { useUiText } from "../../locale/UiLocale";
import { classNames } from "../../classNames";
import { tableMinWidth } from "./columnLayout";
import { DataTableBody } from "./DataTableBody";
import { DataTableHead } from "./DataTableHead";
import { DataTablePager } from "./DataTablePager";
import { DataTableRows } from "./DataTableRows";
import { DataTableTitle } from "./DataTableTitle";
import { DataTableSearch, DataTableToolbar, DataTableTools, DensityButton } from "./DataTableToolbar";
import type { DataTableProps } from "./DataTableTypes";
import { ExportCsvButton } from "./ExportCsvButton";
import { FilterPills } from "./FilterPills";
import { bodyState } from "./rowState";
import { MINIMAL_SKELETON_ROWS, SKELETON_ROWS, skeletonRowCount } from "./skeleton";
import { useExpandedRows } from "./useExpandedRows";
import { usePaging } from "./usePaging";
import { useScrollToTop } from "./useScrollToTop";
import { useSortState } from "./useSortState";
import { useTablePreferences } from "./useTablePreferences";
import { useTableSearch } from "./useTableSearch";
import { useVisibleRows } from "./useVisibleRows";

export type { CellContext, Column, ColumnFilter, ColumnType, DataTableEmpty, DataTableProps } from "./DataTableTypes";
export { highlight } from "./highlight";

const DEFAULT_PAGE_SIZE = 25;
const DEFAULT_PAGE_SIZES = [10, 25, 50, 100];

/**
 * Every list of records uses DataTable: search with highlighting, per-column
 * filter chips, sorting, density, CSV export, pagination, skeleton rows,
 * empty/error states, row actions and expandable rows.
 */
export function DataTable<R extends object>({
  columns,
  rows,
  rowKey,
  id,
  title,
  description,
  toolbar,
  loading = false,
  empty,
  error,
  searchable = true,
  searchPlaceholder,
  searchText,
  pageSize: initialPageSize = DEFAULT_PAGE_SIZE,
  pageSizes = DEFAULT_PAGE_SIZES,
  defaultSort,
  defaultDensity = "comfortable",
  exportName,
  onRowClick,
  renderExpanded,
  footer,
  minimal = false,
  autoHeight = false,
  className,
  ariaLabel,
}: DataTableProps<R>): React.JSX.Element {
  const t = useUiText();
  const search = useTableSearch();
  const { sort, toggleSort } = useSortState(defaultSort);
  const preferences = useTablePreferences(id, { density: defaultDensity, pageSize: initialPageSize });
  const expandedRows = useExpandedRows();
  const visibleRows = useVisibleRows({ rows, columns, query: search.query, filters: search.filters, searchText, sort });
  // Minimal tables show every row on a single page.
  const pageSize = minimal ? Math.max(visibleRows.length, 1) : preferences.pageSize;
  const paging = usePaging(visibleRows.length, pageSize, search);
  const pageRows = visibleRows.slice(paging.start, paging.start + pageSize);
  const scrollRef = useScrollToTop<HTMLDivElement>(paging.currentPage);
  const state = bodyState({ loading, failed: Boolean(error), rowCount: rows.length, pageRowCount: pageRows.length });
  const showPager = !minimal && !loading && !error && visibleRows.length > 0;

  return (
    <div
      className={classNames(
        "nb-card",
        "nb-dt",
        preferences.density === "compact" && "is-compact",
        (autoHeight || minimal) && "is-auto-height",
        className,
      )}
    >
      {title ? <DataTableTitle title={title} description={description} /> : null}

      {minimal ? null : (
        <DataTableToolbar>
          {searchable ? <DataTableSearch query={search.query} placeholder={searchPlaceholder ?? t("Search…")} onQueryChange={search.setQuery} /> : null}
          <FilterPills columns={columns} rows={rows} search={search} />
          <DataTableTools extra={toolbar}>
            <DensityButton density={preferences.density} onToggle={preferences.toggleDensity} />
            {exportName ? (
              <ExportCsvButton fileName={exportName} columns={columns} rows={visibleRows} disabled={loading || visibleRows.length === 0} />
            ) : null}
          </DataTableTools>
        </DataTableToolbar>
      )}

      <div className="nb-dt-scroll" ref={scrollRef}>
        <table
          className="nb-table"
          style={{ minWidth: tableMinWidth(columns) }}
          aria-label={ariaLabel ?? (typeof title === "string" ? title : undefined)}
          aria-busy={loading || undefined}
        >
          <DataTableHead columns={columns} sort={sort} onSort={toggleSort} />
          <DataTableBody
            state={state}
            columns={columns}
            skeletonRows={skeletonRowCount(pageSize, minimal ? MINIMAL_SKELETON_ROWS : SKELETON_ROWS)}
            error={error}
            empty={empty}
            onClearSearch={search.clear}
          >
            <DataTableRows
              rows={pageRows}
              columns={columns}
              query={search.query}
              rowKey={rowKey}
              expandedRows={expandedRows}
              onRowClick={onRowClick}
              renderExpanded={renderExpanded}
            />
          </DataTableBody>
        </table>
      </div>

      {showPager ? (
        <DataTablePager
          paging={paging}
          pageRowCount={pageRows.length}
          visibleCount={visibleRows.length}
          totalCount={rows.length}
          pageSize={preferences.pageSize}
          pageSizes={pageSizes}
          onPageSizeChange={preferences.setPageSize}
        />
      ) : null}
      {footer}
    </div>
  );
}
